// components/MultiFileUploader.js
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";
import { Controller, useWatch } from "react-hook-form";
import FilePreviewCard from "./FilePreviewCard";
import { useTranslation } from "react-i18next";
import ValidationErrorText from "../ValidationErrorText";
import { Box, LabelStyled, SpanStyled } from "../../../styled/Elements";
import { CloudUpload } from "../../../styled/AllImages"; // your icon path
import UrlPreviewCard from "./UrlPreviewCard";

const UploaderWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  & > div {
    width: ${({ width }) => width && width};
    min-width: 450px;
  }
`;

const AddCard = styled.div`
  width: 110px;
  height: 110px;
  background-color: var(--bgPrimary);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text2);
  font-size: 32px;
  font-weight: 300;

  &:hover {
    background-color: var(--bgSecondary);
  }
`;

const DropzoneContainer = styled.div`
  background-color: var(--bgPrimary);
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  color: var(--text);
  transition: border-color 0.3s ease;
  position: relative;

  &:hover {
    background-color: var(--bgSecondary);
  }

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UploadIcon = styled.div`
  font-size: 24px;
  display: flex;
  justify-content: center;
  margin-bottom: 6px;

  .icon-box {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    background-color: var(--white);
  }
`;

const MultiFileUploader = ({
  width,
  name,
  control,
  label,
  maxFiles = 5,
  weight,
  acceptType = "all",
  rules = {},
}) => {
  const { t } = useTranslation();
  const files = useWatch({ control, name }) || [];

  const handleDrop = useCallback(
    (acceptedFiles, onChange) => {
      const updated = [...files, ...acceptedFiles].slice(0, maxFiles);
      onChange(updated);
    },
    [files, maxFiles]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: () => {},
    multiple: true,
    maxSize: 2 * 1024 * 1024, // 2MB
    accept:
      acceptType === "image"
        ? { "image/*": [] }
        : acceptType === "video"
        ? { "video/*": [] }
        : acceptType === "application/pdf"
        ? { "application/pdf": [] }
        : acceptType === "all"
        ? undefined
        : undefined,
    noClick: true,
  });

  return (
    <Box>
      {label && (
        <LabelStyled className="mb-2" weight={weight ? weight : ""}>
          {t(label)}
        </LabelStyled>
      )}

      <Controller
        name={name}
        control={control}
        rules={{
          ...rules,
          validate: (value) =>
            !value ||
            value.length <= maxFiles ||
            t("max_files_limit", { count: maxFiles }),
        }}
        render={({ field, fieldState }) => {
          const currentFiles = field.value || [];

          return (
            <>
              {currentFiles?.length === 0 ? (
                <DropzoneContainer
                  {...getRootProps()}
                  onClick={() =>
                    document
                      .getElementById(`multi-upload-input-${name}`)
                      ?.click()
                  }
                >
                  <input
                    {...getInputProps()}
                    id={`multi-upload-input-${name}`}
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files || []);
                      handleDrop(newFiles, field.onChange);
                    }}
                  />
                  <UploadIcon>
                    <Box className="icon-box">
                      <CloudUpload />
                    </Box>
                  </UploadIcon>
                  <SpanStyled weight="bold" size="14px">
                    {t("click_to_upload")}
                  </SpanStyled>
                  <SpanStyled color="var(--text2)" size="14px">
                    &nbsp; {t("or_drag_drop")}
                    <br />
                    {t("max_file_size")}: 2 MB
                  </SpanStyled>
                </DropzoneContainer>
              ) : (
                <UploaderWrapper width={width && width}>
                  {currentFiles?.map((file, index) => (
                    <FilePreviewCard
                      key={index}
                      file={file}
                      onRemove={() => {
                        const newFiles = [...currentFiles];
                        newFiles.splice(index, 1);
                        field.onChange(newFiles);
                      }}
                    />
                  ))}

                  {currentFiles.length < maxFiles && (
                    <AddCard
                      {...getRootProps()}
                      onClick={() =>
                        document
                          .getElementById(`multi-upload-input-${name}`)
                          ?.click()
                      }
                    >
                      +
                      <input
                        {...getInputProps()}
                        id={`multi-upload-input-${name}`}
                        onChange={(e) => {
                          const newFiles = Array.from(e.target.files || []);
                          handleDrop(newFiles, field.onChange);
                        }}
                      />
                    </AddCard>
                  )}
                </UploaderWrapper>
              )}

              {fieldState?.error && (
                <ValidationErrorText
                  text={
                    t(fieldState?.error?.message) ||
                    `${t(label || name)} ${t("isRequired")}`
                  }
                />
              )}
            </>
          );
        }}
      />
    </Box>
  );
};

export default MultiFileUploader;
